import React, { useState, useEffect } from 'react'
import { map } from 'lodash'
import { useHistory } from 'react-router-dom'
import cn from 'classnames'

import OrderInformation from './OrderInformation'
import CardReader from '../../CardReader'
import SidebarModal from '../../SidebarModal'
import Button from '../../Button'
import BackChevron from '../../BackChevron'
import RadioSelectionModal from '../../RadioSelectionModal'
import { centsToDollars } from '../../../utils/formatters'

import styles from './Close.module.scss'

const CloseTender = ({
  cashEnabled,
  className,
  deviceReceiptPrinterUuid,
  fetchPrinters,
  isOrderLatestFromServer,
  noOpPayments,
  onCashPayment,
  onClose,
  onPaymentSelect,
  onViewCheck,
  orderId,
  orderNumber,
  payment,
  paymentId,
  peripheralStatus,
  printOrder,
  printers,
  suiteInformation,
  tenderAmountInCents,
  pendingMutation,
  payments,
  total,
  isTenderUpdate,
}) => {
  const classNames = cn(styles.close, className)
  const history = useHistory()
  const orderLoading = !(pendingMutation === undefined)

  const [closeWithNewCard, setCloseWithNewCard] = useState(false)
  const [savedPaymentsFlag, setSavedPaymentsFlag] = useState(isTenderUpdate)
  const [customPaymentFlag, setCustomPaymentFlag] = useState(false)
  const [showPrinterSelection, setShowPrinterSelection] = useState(false)
  const [paymentSelected, setPaymentSelected] = useState(false)

  useEffect(() => {
    setPaymentSelected(isTenderUpdate)
  }, [isTenderUpdate])

  useEffect(() => {
    fetchPrinters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePrint = () => {
    if (!deviceReceiptPrinterUuid) {
      setShowPrinterSelection(!showPrinterSelection)
    } else {
      printOrder({
        printType: 'suite-receipt-auth',
        printerUuids: [deviceReceiptPrinterUuid],
        orderUuid: orderId,
        paymentUuid: paymentId,
      })
    }
  }

  const handleOnFinalize = () => {
    history.push(`/tabs/${orderId}/finalize-tender/${paymentId}`)
  }

  const printerSelection = (
    <RadioSelectionModal
      show={showPrinterSelection}
      toggleShow={setShowPrinterSelection}
      items={printers}
      title={"Print Receipt"}
      selectionTitle={"Select a printer"}
      buttonText={"PRINT"}
      className={classNames}
      onDone={(printerSelected) =>
        printOrder({
          printType: "suite-receipt-auth",
          printerUuids: [printerSelected?.uuid],
          orderUuid: orderId,
          paymentUuid: paymentId,
        })
      }
    />
  )

  const makeSavedPaymentButton = (payment) => (
    <Button
      key={payment.uuid}
      disabled={!isOrderLatestFromServer}
      onClick={() => {
        onPaymentSelect({ payment, useExistingTender: true })
        setPaymentSelected(true)
      }}
      className={styles.actionButton}
    >
      {payment?.shortDescription}
    </Button>
  )

  const makeCustomTenderButton = (customTender) => (
    <Button
      key={customTender.name}
      disabled={!isOrderLatestFromServer}
      onClick={() => {
        onPaymentSelect({ customTender })
        setPaymentSelected(true)
      }}
      className={styles.actionButton}
    >
      {customTender?.displayName}
    </Button>
  )

  let buttons = [
    cashEnabled && <Button key='cash' onClick={() => onCashPayment(tenderAmountInCents)} className={styles.actionButton} disabled={orderLoading || !isOrderLatestFromServer}>CASH</Button>,
    <Button key='card' onClick={() => setCloseWithNewCard(true)} className={styles.actionButton} disabled={orderLoading ||  !isOrderLatestFromServer}>Card Reader</Button>,
    (noOpPayments.length >= 1) && <Button key='custom payment' onClick={() => setCustomPaymentFlag(true)} className={styles.actionButton} disabled={orderLoading}>Other</Button>,
    (payments.length >= 1) && <Button key='saved payment' onClick={() => setSavedPaymentsFlag(true)} className={styles.actionButton} disabled={orderLoading}>Saved Payments</Button>,
  ]

  if (savedPaymentsFlag) {
    buttons = [
      ...map(payments, makeSavedPaymentButton),
    ]
  }

  if (customPaymentFlag) {
    buttons = [
      ...map(noOpPayments, makeCustomTenderButton),
    ]
  }

  if (paymentId && paymentSelected) {
    buttons = [
      <Button disabled={orderLoading} key="Review & Sign Screen" onClick={() => onViewCheck(paymentId)} className={styles.actionButton}>Review & Sign Screen</Button>,
      <Button disabled={orderLoading} key="Print Slip" onClick={handlePrint} className={styles.actionButton}>Print Slip</Button>,
      <Button disabled={orderLoading} key="Finalize" onClick={handleOnFinalize} className={styles.actionButton}>Finalize</Button>,
    ]
  }

  if (closeWithNewCard) {
    buttons = []
  }

  const orderInformationProps = {
    account: suiteInformation?.displayName,
    grandTotal: total,
    orderNumber,
    tenderAmount: centsToDollars(tenderAmountInCents),
    hidePayment: true,
  }

  let cardReaderComponent = closeWithNewCard &&
    <CardReader
        uuid={orderId}
        total={centsToDollars(tenderAmountInCents).replace('$', '')}
        onCancel={() => setCloseWithNewCard(false)}
        onSuccess={(cardReaderData) => {
        onPaymentSelect({ useNewCard: true, cardReaderData })
        setCloseWithNewCard(false)
        setPaymentSelected(true)
        }}
        onDone={onClose}
        authorizationType="auth"
        peripheralStatus={peripheralStatus}
        orderNumber={orderNumber}
    />


  const leftAccessoryElement = (
    <BackChevron onClick={() => {
      if (paymentSelected) {
        setPaymentSelected(false)
      } else
      if (savedPaymentsFlag) {
        setSavedPaymentsFlag(false)
      } else 
      if (customPaymentFlag){
        setCustomPaymentFlag(false)
      } else {
        history.replace(`/tabs/${orderId}`)
      }
    }} />
  )

  return (
    <SidebarModal
      show
      title={"Close Tender"}
      onClose={onClose}
      leftAccessory={leftAccessoryElement}
      className={classNames}
    >
      <div className={styles.bodyContainer}>
        <OrderInformation {...orderInformationProps} />
          {showPrinterSelection && printerSelection}
          {cardReaderComponent}
          <div className={styles.buttonContainer}>
            {buttons}
          </div>
      </div>
    </SidebarModal>
  )
}

export default CloseTender
